import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useInterval from 'use-interval'
import { useAppDispatch } from 'store/hooks'
import { actions as companyActions } from 'store/companySearch'
import { RootState } from 'store/'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import { Link } from 'react-router-dom'
import Step from './components/Step'
import { stepsData } from './stepsData'
import { CompanySearchForMultisectorStatus, Stats } from 'store/companySearch/types'
import ErrorComponent from 'containers/search/companySearch/companySearchWaitPage/components/ErrorComponent'
import { useIsBoAdminRole } from 'hooks/useIsBoAdminRole'
import { CompanySearchLogs } from 'containers/search/components/CompanySearchLogs'

function CompanySearchWaitPage() {
  const SEARCHES_FINISHED = 'searches_finished'
  const dispatch = useAppDispatch()
  const isAdminRole = useIsBoAdminRole()

  const [showLogs, setShowLogs] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const companySearchResponse = useSelector((state: RootState) => state.companySearch.searchResponse)
  const companySearchStatusRequestError = useSelector((state: RootState) => state.companySearch.error.searchStatus)
  const status: CompanySearchForMultisectorStatus | undefined = companySearchResponse?.status
  const stats: Stats | undefined = companySearchResponse?.stats
  const multisectorId = companySearchResponse?.completion_action_id
  const companyNamesSelectedList = companySearchResponse?.company_names.split('||||')
  const companyNamesWithAliasesArray = stats?.companyNamesWithAliases?.split('||||')
  const { id } = useParams<{ id?: string }>()

  const STATUS_TO_STEP: Record<CompanySearchForMultisectorStatus, number> = {
    initializing: 1,
    fetching_xml: 2,
    winning_patent: 3,
    winning_patent_finished: 4,
    searches_queued: 4,
    searches_processing: 5,
    searches_finished: 6,
    error: 6,
  }

  const getCurrentStep = (status?: CompanySearchForMultisectorStatus): number => {
    if (!status || status === 'initializing') {
      return 1
    }

    return STATUS_TO_STEP[status] ?? 1
  }
  const currentStep = getCurrentStep(status)

  useEffect(() => {
    if (id) dispatch(companyActions.getStatus(id))
  }, [id, dispatch])

  useInterval(
    () => {
      if (id) dispatch(companyActions.getStatus(id))
    },
    status !== SEARCHES_FINISHED && status !== 'error' ? 5000 : null,
  )

  const getDynamicText = (text: string) => {
    if (!stats) return text

    return text
      .replace('{{initial_patents_count}}', stats.initialPatentsCount?.toString() || 'N/A')
      .replace('{{cleaned_patents_count}}', stats.cleanedPatentsCount?.toString() || 'N/A')
      .replace('{{initial_ipcr_codes_count}}', stats.initialIpcrCodesCount?.toString() || 'N/A')
      .replaceAll('{{winning_ipcr_codes_count}}', stats.winningIpcrCodes?.length.toString() || 'N/A')
      .replaceAll('{{winning_ipcr_codes}}', stats.winningIpcrCodes?.join(', ') || 'N/A')
      .replace('{{winning_patents_count}}', stats.winningPatents?.length.toString() || 'N/A')
  }

  const regenerateCompanySearch = () => {
    if (id) {
      dispatch(companyActions.regenerateCompanySearch(id))
      setIsRegenerating(true)
      // wait a bit and then refresh status
      setTimeout(() => {
        dispatch(companyActions.getStatus(id))
        setIsRegenerating(false)
      }, 3000)
    }
  }

  return (
    <Box m={5}>
      {companySearchStatusRequestError && (
        <Typography color="error" mt={1}>
          {companySearchStatusRequestError}
        </Typography>
      )}
      {companySearchResponse && (
        <>
          <Typography variant="h4">
            Multisector Report generation
            {status !== SEARCHES_FINISHED && status !== 'error' && (
              <>
                .{' '}
                <Typography variant="h4" component="span" color="primary">
                  Processing step {currentStep} of 6.
                </Typography>
              </>
            )}
          </Typography>

          {status === 'error' && (
            <Box mt={2}>
              <ErrorComponent stats={stats} />
            </Box>
          )}
          {status !== 'error' && (
            <Box>
              <Typography variant="h6" sx={{ mt: 3 }}>
                Company Name(s):
                <Tooltip title={companyNamesWithAliasesArray?.join(', ')}>
                  <Typography variant="h6" component="span" color="primary" sx={{ textTransform: 'capitalize' }}>
                    {' '}
                    {companyNamesSelectedList?.join(', ')}{' '}
                    {companyNamesWithAliasesArray &&
                      companyNamesSelectedList &&
                      companyNamesWithAliasesArray?.length > companyNamesSelectedList?.length && (
                        <>({companyNamesWithAliasesArray.length})</>
                      )}
                  </Typography>
                </Tooltip>
              </Typography>
              <Typography fontSize={14} sx={{ mt: 5 }}>
                We’ve launched a deep technical scan across the Insights IDS patent landscape. Due to the depth and scale of this
                analysis, your report may take <strong>5–15 minutes</strong> to complete.
                <br />
                This is no ordinary search and report process. We’re analysing and comparing IP content, connections,
                technologies, and competitors by processing <strong>160 million patents</strong>, mapping them across multiple
                industries, subgroup taxonomies, and semantic signals.
                <br />
                Don’t worry, we’ll email you as soon as it’s ready. You can safely close this screen, or follow the
                <Link to="#"> Processing Steps </Link>
                below.
              </Typography>
              <Box m={4}>
                {stepsData.map(step => {
                  const dynamicText = getDynamicText(step.text)
                  return (
                    <Step
                      key={step.step}
                      stepNumber={step.step}
                      isProcessing={currentStep === step.step && status !== SEARCHES_FINISHED}
                      isCompleted={currentStep > step.step || status === SEARCHES_FINISHED}
                      title={step.title}
                      text={dynamicText}
                    />
                  )
                })}
              </Box>
            </Box>
          )}

          {status === SEARCHES_FINISHED && multisectorId && (
            <Box display="flex" justifyContent="center" mb={7}>
              <Button
                size="large"
                component={Link}
                to={`/dashboard/reporting/company/${multisectorId}`}
                variant="contained"
                color="primary">
                View your Multisector report
              </Button>
            </Box>
          )}
          {isAdminRole && (
            <Box mt={3}>
              <Button sx={{ mb: 3 }} variant="contained" disabled={isRegenerating} onClick={() => regenerateCompanySearch()}>
                Regenerate company search (Bo admin only)
              </Button>
              <CompanySearchLogs status={status} stats={stats} />
            </Box>
          )}
        </>
      )}
    </Box>
  )
}

export default CompanySearchWaitPage
